import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const LOGO_URL =
  "/logo-brand.png";

function currency(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

export default function ContractPage() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <DashboardShell>
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Legal</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Service Agreement</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Printable contract that auto-fills with the current dealership name, pricing, and contract term
            from Settings. Print and have the dealer principal sign.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-contract-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print Contract
        </Button>
      </div>

      <div
        id="contract"
        className="bg-white text-neutral-900 max-w-[8.5in] mx-auto shadow-2xl border border-border rounded-lg overflow-hidden"
        style={{ pageBreakAfter: "avoid" }}
      >
        {/* Header — logo blends into navy (no chunky white box) */}
        <div className="bg-navy text-white px-10 py-7 flex items-center gap-5">
          <img
            src={LOGO_URL}
            alt="Decline Drive"
            className="w-40 h-40 object-contain flex-shrink-0"
          />
          <div>
            <div className="font-display font-black text-3xl tracking-tight">Decline Drive™ Service Agreement</div>
            <div className="text-xs text-[hsl(var(--gold-light))] mt-1">
              Ayers Enterprises
            </div>
          </div>
        </div>
        <div className="gold-divider" />

        {/* Body */}
        <div className="px-10 py-8 text-sm leading-relaxed space-y-5">
          <div>
            <div className="font-mono-label text-neutral-500 mb-1">Effective date</div>
            <div className="font-semibold">{today}</div>
          </div>

          <div>
            This Service Agreement (the &quot;<b>Agreement</b>&quot;) is entered into between{" "}
            <b>Ayers Enterprises</b> (&quot;<b>Provider</b>&quot;) and{" "}
            <b>{s?.dealership_name || "________________________"}</b> (&quot;<b>Client</b>&quot;).
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">1. Services</div>
            Provider shall furnish Client with access to the Decline Drive platform, an automated
            declined-service recovery system that sends timed SMS offers to the Client&apos;s customers,
            manages appointment scheduling, and reports conversion analytics.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">2. Fees</div>
            <ul className="list-disc ml-5 space-y-1">
              <li>
                <b>One-time onboarding fee</b> of {currency(s?.onboarding_fee || 895)}, due on execution of this
                Agreement.
              </li>
              <li>
                <b>Monthly subscription fee</b> of {currency(s?.monthly_fee || 1599)} per rooftop, billed on the
                first of each calendar month.
              </li>
              {(s?.fee_percent || 0) > 0 && (
                <li>
                  <b>Recovery fee</b> of {s.fee_percent}% of gross recovered service revenue, billed monthly in
                  arrears based on RO totals recorded in the platform.
                </li>
              )}
            </ul>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">3. Term</div>
            This Agreement shall remain in effect for an initial term of{" "}
            <b>{s?.contract_months || 12} months</b> from the Effective Date. Thereafter it shall auto-renew for
            successive one-month terms unless either party provides thirty (30) days&apos; written notice of
            non-renewal.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">4. Payment</div>
            All fees are payable within fifteen (15) days of invoice. Client may pay by check made payable to{" "}
            <b>{s?.remit_to_name || "Ayers Enterprises"}</b>
            {s?.remit_to_address ? (
              <>
                {" "}
                and mailed to <span className="whitespace-pre-line">{s.remit_to_address}</span>
              </>
            ) : (
              ""
            )}
            {s?.remit_ach_bank ? `, or by ACH transfer to ${s.remit_ach_bank}.` : "."}
            Past-due amounts accrue interest at 1.5% per month.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">5. Onboarding Guarantee</div>
            If Provider fails to make the platform operational and send the Client&apos;s first customer SMS
            within thirty (30) days of the Effective Date, Client may terminate this Agreement and receive a
            full refund of the onboarding fee.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">6. Data &amp; Confidentiality</div>
            Client owns all customer data uploaded to the platform. Provider agrees to keep such data
            confidential, use it solely to deliver the Services, and comply with all applicable
            telecommunications and privacy laws (including the TCPA). Customer opt-out (&quot;STOP&quot;)
            requests will be honored automatically.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">7. DMS Data Access</div>
            Client authorizes Provider to establish, on Client&apos;s behalf, a nightly data feed of declined
            service records from Client&apos;s Dealer Management System (&quot;DMS&quot;), either through
            (a)&nbsp;a direct API integration with Client&apos;s DMS vendor, (b)&nbsp;a licensed data aggregator
            such as DealerVault (Authenticom), Fortellis, or IntegraLink, or (c)&nbsp;manual CSV upload by
            Client&apos;s staff. Client shall sign any data-consent forms reasonably required by the DMS vendor
            or aggregator. Provider shall use such data solely to deliver the Services and shall not resell it
            to third parties. Aggregator subscription fees are included in Client&apos;s monthly subscription
            unless separately itemized on the Client&apos;s statement.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">8. Limitation of Liability</div>
            Provider&apos;s aggregate liability under this Agreement shall not exceed fees paid by Client in
            the three (3) months preceding any claim. Provider shall not be liable for indirect, incidental, or
            consequential damages.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">9. Governing Law</div>
            This Agreement shall be governed by the laws of the state in which Provider is organized, without
            regard to conflict-of-law principles.
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">10. Entire Agreement</div>
            This document constitutes the entire agreement between the parties and supersedes all prior
            discussions. Amendments must be in writing and signed by both parties.
          </div>

          {/* Signatures — must stay on one page, glued to the PROVIDER/CLIENT header */}
          <div className="pt-6 grid grid-cols-2 gap-8 signature-block">
            <div className="signature-col">
              <div className="font-display font-bold mb-1">PROVIDER</div>
              <div className="text-sm text-neutral-700">Ayers Enterprises</div>
              <div className="relative mt-6 border-b border-neutral-400 h-10">
                <span className="signature-cursive text-navy absolute left-1 bottom-1 text-4xl" data-testid="contract-provider-signature">
                  James Ayers
                </span>
              </div>
              <div className="text-[0.65rem] uppercase tracking-widest text-neutral-500 mt-1">
                Authorized signature
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <div className="border-b border-neutral-400 h-6 text-sm text-neutral-800 pl-1 flex items-end pb-0.5">James Ayers</div>
                  <div className="text-[0.6rem] uppercase text-neutral-500">Printed name</div>
                </div>
                <div>
                  <div className="border-b border-neutral-400 h-6 text-sm text-neutral-800 pl-1 flex items-end pb-0.5">{today}</div>
                  <div className="text-[0.6rem] uppercase text-neutral-500">Date</div>
                </div>
              </div>
            </div>
            <div className="signature-col">
              <div className="font-display font-bold mb-1">CLIENT</div>
              <div className="text-sm text-neutral-700">
                {s?.dealership_name || "________________________"}
              </div>
              <div className="mt-6 border-b border-neutral-400 h-8" />
              <div className="text-[0.65rem] uppercase tracking-widest text-neutral-500 mt-1">
                Authorized signature
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <div className="border-b border-neutral-400 h-6 text-sm text-neutral-800 pl-1 flex items-end pb-0.5">
                    {s?.dealership_name || ""}
                  </div>
                  <div className="text-[0.6rem] uppercase text-neutral-500">Printed name (auto)</div>
                </div>
                <div>
                  <div className="border-b border-neutral-400 h-6 text-sm text-neutral-800 pl-1 flex items-end pb-0.5">{today}</div>
                  <div className="text-[0.6rem] uppercase text-neutral-500">Date</div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 text-[0.6rem] text-neutral-500 border-t border-neutral-200 text-center">
            © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises. All rights reserved.
            This contract template is not legal advice; consult an attorney before use.
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
